import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apple, bg, contact, download1, download2, download3, downloadButton, faqBg, feature1, feature2, feature3, featuresShadow, google, logoColor, logoLight, s2Rectangle, sec1, topLeftCorner, topRightCorner, dummyUser, playIcon } from '../../ui/media';
import { useLogout, useUser } from '../../../hooks/useAuth';
import { useGetFAQQuery, useFillContactUsForm } from "../../../hooks/useCommon"
import { PauseCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const LandingPage = () => {
    const [navOpen, setNavOpen] = useState(false);
    const [userData, setUserData] = useState(null);
    const [questions, setQuestions] = useState([])
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [message, setMessage] = useState("")
    const [isSecurityPlaying, setIsSecurityPlaying] = useState(false);
    const [isEventPlaying, setIsEventPlaying] = useState(false);
    const { data: faqData, isSuccess: isFaqSuccess } = useGetFAQQuery(`?language=en`)
    const contactFillMutation = useFillContactUsForm()
    const navigate = useNavigate()
    const dropdownRef = useRef(null);
    const securityRef = useRef(null);
    const eventRef = useRef(null);
    const logout = useLogout()
    const user = useUser()
    const { isError, data } = user
    const [expandedFaq, setExpandedFaq] = useState(0);

    const toggleNav = () => {
        setNavOpen(!navOpen);
    };

    const toggleFaq = (index) => {
        setExpandedFaq(expandedFaq === index ? null : index);
    };

    const handleSubmit = async () => {
        if (name === "" || email === "" || phoneNumber === "" || message === "") {
            toast.error("Please Fill the complete form to continue.")
            return false
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            toast.error('Please enter a valid email address');
            return
        }
        contactFillMutation.mutate({
            name,
            email,
            phone: phoneNumber,
            message
        }, {
            onSuccess: () => {
                setName("")
                setEmail("")
                setPhoneNumber("")
                setMessage("")
                setFormSubmitted(true);
                setTimeout(() => {
                    setFormSubmitted(false);
                }, 3000);
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Unable to Fill Contact Form"
                toast.error(message)
            }
        })
    };

    const handleLogout = () => {
        logout.mutate(null, {
            onSuccess: () => {
                navigate('/');
                toast.success('Logged out successfully');
            },
            onError: () => {
                navigate('/');
                toast.error('Session expired, logged out');
            }
        })
    };

    const playVideo = (ref) => {
        if (ref == "security") {
            setIsSecurityPlaying(!isSecurityPlaying)
            setIsEventPlaying(false)
            if (isSecurityPlaying) {
                securityRef.current.pause()
            } else {
                securityRef.current.play()
            }
            eventRef.current.pause()
        } else if (ref == "events") {
            setIsEventPlaying(!isEventPlaying)
            setIsSecurityPlaying(false)
            if (isEventPlaying) {
                eventRef.current.pause()
            } else {
                eventRef.current.play()
            }
            securityRef.current.pause()
        }
    }

    const handleContextMenu = (e) => {
        e.preventDefault(); // disables right-click menu
    };

    useEffect(() => {
        if (isFaqSuccess) {
            const faqList = faqData?.data?.data || [];
            setQuestions(faqList);
        }
    }, [faqData, isFaqSuccess])

    useEffect(() => {
        if (isError) {
            handleLogout()
        }
        setUserData(data?.data?.data)
    }, [data, isError])

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const hash = window.location.hash;
            if (hash) {
                const el = document.querySelector(hash);
                if (el) {
                    // Smooth scroll into view
                    setTimeout(() => {
                        el.scrollIntoView({ behavior: "smooth" });
                    }, 100); // Small delay to ensure component is mounted
                }
            }
        }
    }, []);

    return (
        <div className="flex flex-col min-h-screen text-white">
            <div style={{ background: `url(${sec1})`, backgroundSize: "100%" }} >
                {/* Navigation */}
                <header className="sticky top-0 z-50 shadow-md bg-white/50">
                    <div className="container mx-auto px-4 py-3 flex justify-between items-center">
                        <div className="flex items-center">
                            <img
                                onClick={() => navigate('/lander')}
                                className="h-12 cursor-pointer"
                                src={logoColor}
                                alt="Logo"
                            />
                        </div>
                        <button onClick={toggleNav} className="md:hidden text-white">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        </button>
                        <nav className={`absolute md:relative top-full left-0 w-full md:w-auto bg-white md:bg-transparent p-4 md:p-0 transform ${navOpen ? 'scale-y-100' : 'scale-y-0 md:scale-y-100'} origin-top transition-transform md:flex md:items-center`}>
                            <ul className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6">
                                {[{
                                    name: 'Home',
                                    url: '/lander'
                                }, {
                                    name: 'About Us',
                                    url: '/lander#about'
                                }, {
                                    name: 'Services',
                                    url: '/lander#services',
                                }, {
                                    name: 'Subscriptions',
                                    url: '/subscriptions',
                                }, {
                                    name: 'FAQ',
                                    url: '/lander#faq'
                                }, {
                                    name: 'Contact Us',
                                    url: '/lander#contact'
                                }].map((item, index) => (
                                    <li key={index?.url}>
                                        <a href={`${item.url}`} className={`text-gray-800`}>{item?.name}</a>
                                    </li>
                                ))}
                                {userData &&
                                    <li key={'dashboard'}>
                                        <a href={`${userData?.user_type === 2 ? '/events/list' : '/locations/list'}`} className={`text-gray-800`}>Dashboard</a>
                                    </li>
                                }
                            </ul>
                            {userData ?
                                <div className="mt-2 md:mt-0 md:px-5 py-1 relative" ref={dropdownRef}>
                                    <button
                                        onClick={() => setDropdownOpen(!dropdownOpen)}
                                        className={`flex items-center font-medium text-gray-800 focus:outline-none`}>
                                        <img
                                            className="h-8 rounded-full mr-2"
                                            src={userData?.profile_pic ? `${process.env.REACT_APP_IMAGE_URL}/${userData?.profile_pic}` : dummyUser}
                                            alt="Profile"
                                        />
                                        <span className="">{userData?.name}</span>
                                        <svg
                                            className="ml-2 h-5 w-5 text-gray-800"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true">
                                            <path
                                                fillRule="evenodd"
                                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                    {dropdownOpen && (
                                        <div className={`absolute md:right-0 mt-2 w-auto rounded-md shadow-lg py-1 bg-white focus:outline-none`}>
                                            <div className="px-4 py-3">
                                                <p className={`text-sm text-gray-800`}>{userData?.name}</p>
                                                <p className={`text-sm text-gray-800 font-medium text-gray-500 truncate`}>
                                                    {userData?.email}
                                                </p>
                                            </div>
                                            <hr />
                                            <a
                                                href={`${userData?.user_type === 2 ? "/events/list" : "/locations/list"}`}
                                                className={`block px-4 py-2 text-sm text-black hover:text-black`}>
                                                Back to Dashboard
                                            </a>
                                            <a
                                                href="/account"
                                                className={`block px-4 py-2 text-sm text-black hover:text-black`}>
                                                Account
                                            </a>
                                            <button
                                                onClick={handleLogout}
                                                className={`block w-full text-left px-4 py-2 text-sm text-black hover:text-black`}>
                                                Log Out
                                            </button>
                                        </div>
                                    )}
                                </div> :
                                <button
                                    onClick={() => navigate('/login')}
                                    className="mt-4 md:mt-0 md:ml-6 bg-blue-600 text-white px-5 py-1 rounded-full font-medium">
                                    Log In
                                </button>
                            }
                        </nav>
                    </div>
                </header>
                {/* Hero Section */}
                <section className="relative h-screen">
                    <div className="container mx-auto px-4 py-16 md:py-24 flex flex-col md:flex-row items-center h-full">
                        <div className="z-10">
                            <h1 className="text-[3.6rem] leading-[65px] poppins-extrabold mb-6">Your Live View,<br />Just a Tap Away</h1>
                            <p className="text-[1.2rem] leading-[30px] tracking-wider poppins-regular mb-8">Watch the action unfold and interact<br />seamlessly. It's like being there, live.</p>
                            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                                <button
                                    onClick={() => navigate(userData ? userData?.user_type === 2 ? "/events/list" : "/locations/list" : "/register")}
                                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-3 rounded-full font-medium transition">
                                    Get Started
                                </button>
                                <a 
                                    href='https://apps.apple.com/us/app/viewport-online/id6755670618'
                                    target='_blank'>
                                    <button className="bg-transparent border border-white hover:bg-white hover:text-blue-800 text-white px-8 py-3 rounded-full font-medium transition">Download App</button>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            {/* About Us Section */}
            <section id="about" className="relative py-16">
                <div className="absolute inset-0" style={{ background: `url(${s2Rectangle})`, backgroundSize: 'cover' }} />
                <div className="absolute inset-0 bg-[#13448E]/10" />
                <div className="relative container mx-auto px-4 z-10 text-white">
                    <h2 className="text-[3.5rem] leading-[65px] poppins-extrabold text-center mb-12">About Us</h2>
                    <div className="max-w-3xl mx-auto text-center">
                        <h3 className="text-2xl poppins-regular mb-8">HD Streaming. Zero Lag. Unlimited Access</h3>
                        <p className="text-xl mb-8">Powered by cutting-edge tech to deliver seamless live experiences.<br />We’re fans first. That’s why our app puts you center stage.</p>
                        <ul className="space-y-2 mb-8">
                            {[
                                'Crystal-clear HD streaming with minimal buffering',
                                'Comprehensive coverage of your preferred events from multiple angles',
                                'Secure and encrypted streaming for your peace of mind',
                            ].map((item, i) => (
                                <li key={i} className="flex items-start">
                                    <svg className="w-5 h-5 text-blue-400 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-base">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium">Learn More</button>
                    </div>
                </div>
            </section>
            {/* Services and App Download Section */}
            <div className="p-0 m-0" style={{ background: `url(${bg})`, backgroundSize: "100% 100%" }}>
                {/* Services Section */}
                <section id="services" className="py-16 m-0">
                    <div className="container mx-auto px-4">
                        {/* Header */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                            <h2 className="text-5xl poppins-extrabold text-left">Our Services</h2>
                            <p className="text-base poppins-regular text-left">
                                We offer specialized streaming services tailored to your specific needs.
                                We're here first. That's why our app puts you center-stage.
                            </p>
                        </div>

                        {/* Security & Events */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                            {[{
                                video: process.env.REACT_APP_SECURITY_MOVIE_LINK,
                                ref: 'security',
                                title: 'SECURITIES',
                                description: 'Powered by cutting-edge tech to deliver seamless live monitoring and playback. Secure HD feed with your own private stream.'
                            }, {
                                video: process.env.REACT_APP_SECURITY_MOVIE_LINK,
                                ref: 'events',
                                title: 'EVENTS',
                                description: 'Powered by cutting-edge tech to deliver seamless live coverage from across the globe. Share every moment.'
                            }].map((item, index) => (
                                <div key={index} className="rounded-lg overflow-hidden" style={{ background: `url(${featuresShadow})` }}>
                                    <div className="relative h-85">
                                        <video ref={item?.ref == 'security' ? securityRef : eventRef} loop={true} onContextMenu={handleContextMenu} muted={true} controls={false} autoPlay={false} className="w-full h-full object-cover">
                                            <source src={item.video} />
                                        </video>
                                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                            <button onClick={() => playVideo(item?.ref)} className="flex items-center justify-center">
                                                {item.ref == 'security' ?
                                                    isSecurityPlaying ? <PauseCircle className="text-4xl" /> : <img className='h-16' src={playIcon} />
                                                    : item.ref == 'events' ? isEventPlaying ? <PauseCircle className="text-4xl" /> : <img className='h-16' src={playIcon} />
                                                        : null
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                                        <p className="mb-6">{item.description}</p>
                                        <a 
                                            href='https://apps.apple.com/us/app/viewport-online/id6755670618'
                                            target='_blank'>
                                            <button>
                                                <img src={downloadButton} alt="Download Button" className="h-12" />
                                            </button>
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Additional Services */}
                        <div className="mb-12 rounded-lg" style={{ background: `url(${featuresShadow})` }}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
                                {[{
                                    image: feature1,
                                    title: 'Real-Time Viewing & Interaction',
                                    description: 'Watch and interact with real-time feedback for enhanced streaming experiences'
                                }, {
                                    image: feature2,
                                    title: 'Sharing in Seconds • Major Time',
                                    description: 'Instantly share important moments with your network through our seamless interface'
                                }, {
                                    image: feature3,
                                    title: 'Engage with Live Streaming',
                                    description: 'Connect with audiences in real-time through our immersive live streaming platform'
                                }].map((feature, idx) => (
                                    <div key={idx}>
                                        <img src={feature.image} alt={feature.title} className="rounded mb-4" />
                                        <h4 className="text-xl font-semibold mb-2">{feature.title}</h4>
                                        <p className="text-sm text-blue-100">{feature.description}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="text-center py-6">
                                <a 
                                    href='https://apps.apple.com/us/app/viewport-online/id6755670618'
                                    target='_blank'>
                                    <button>
                                        <img src={downloadButton} alt="Download Button" className="h-20" />
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
                {/* App Download Section */}
                <section>
                    <div className="container mx-auto px-4 relative">
                        <h2 className="text-4xl poppins-extrabold text-center mb-4">Download the App Now</h2>
                        <p className="text-lg tracking-wide poppins-regular text-center mb-12">
                            Experience the best features right at your fingertips. Download<br />
                            our app now and unlock exclusive benefits!
                        </p>
                        {/* Store Links */}
                        <div className="flex flex-col md:flex-row items-center justify-center mb-10 space-y-4 md:space-y-0 md:space-x-4">
                            <a target='_blank' href="https://play.google.com/store/apps/details?id=com.viewportlives"><img src={google} alt="Get it on Google Play" className="h-12" /></a>
                            <a target='_blank' href="https://apps.apple.com/us/app/viewport-online/id6755670618"><img src={apple} alt="Download on the App Store" className="h-12" /></a>
                        </div>
                        {/* Decorative Corners */}
                        <img src={topLeftCorner} alt="Top Left Decoration" className="hidden md:block absolute top-[220px] left-[20px] h-72" />
                        <img src={topRightCorner} alt="Top Right Decoration" className="hidden md:block absolute right-[30px] top-[730px] h-72" />
                        {/* Screenshots */}
                        <div className="flex flex-col md:flex-row justify-center items-start space-y-16 md:space-y-0 md:space-x-16 mb-8 md:h-[825px]"  >
                            <img src={download1} alt="App login screen" className="shadow-lg md:mt-[70px] h-[400px] md:h-[600px] md:mx-0 mx-auto" />
                            <img src={download2} alt="App content feed" className="shadow-lg h-[400px] md:h-[600px] md:mx-0 mx-auto" />
                            <img src={download3} alt="App gallery view" className="shadow-lg h-[400px] md:h-[600px] md:mx-0 mx-auto" style={{ marginTop: 65 }} />
                        </div>
                    </div>
                </section>
            </div>
            {/* FAQ Section */}
            <section id="faq" className="bg-blue-700 py-16" style={{ backgroundImage: `url(${faqBg})`, backgroundSize: "100%" }}>
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-extrabold poppins-extrabold text-center mb-2">
                        Frequently asked questions
                    </h2>
                    <p className="text-lg tracking-wide poppins-regular text-center mx-auto mb-12">
                        Got Questions? We’ve Got Answers. Skip the Wait—Find Instant Solutions Here.
                        <br />Can’t Find It? Email Us!
                    </p>
                    <div className="max-w-3xl mx-auto space-y-4">
                        {questions?.filter((q) => q?.status === 1)?.map((item, index) => (
                            <div key={index} className="border bg-blue-900 rounded-lg overflow-hidden">
                                <button
                                    className="w-full flex justify-between items-center p-4 text-left"
                                    onClick={() => toggleFaq(index)}
                                    aria-expanded={expandedFaq === index}>
                                    <span className="font-medium">{item.question}</span>
                                    <svg
                                        className={`w-5 h-5 transform transition-transform ${expandedFaq === index ? "rotate-180" : ""
                                            }`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {expandedFaq === index && (
                                    <div className="px-4 pb-4 text-blue-200">
                                        <p>{item.answer}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            {/* Contact Section and Footer */}
            <div style={{ backgroundImage: `url(${bg})`, backgroundSize: "100% 100%" }}>
                {/* Contact Section */}
                <section id="contact" className="py-16">
                    <div
                        className="container mx-auto px-8 py-16"
                        style={{ backgroundImage: `url(${featuresShadow})`, backgroundSize: "100% 100%" }}>
                        <div className="flex flex-col md:flex-row gap-8">
                            <div className="md:w-1/2">
                                <img
                                    src={contact}
                                    alt="Person using smartphone"
                                    className="rounded-lg w-full object-cover h-[480px]"
                                />
                            </div>
                            <div className="md:w-1/2">
                                <h2 className="text-4xl leading-tight font-extrabold poppins-extrabold mb-4">
                                    Contact Us
                                </h2>
                                <p className="text-lg poppins-regular mb-6">
                                    Have a query? Contact us! We look forward to hearing from you.
                                </p>

                                {formSubmitted ? (
                                    <div className="bg-blue-900 text-white p-4 rounded-lg text-center">
                                        <p>Thank you for your message! We'll get back to you soon.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-medium mb-1">
                                                Name
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                className="w-full px-4 py-2 rounded-md bg-blue-900 border border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium mb-1">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full px-4 py-2 rounded-md bg-blue-900 border border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="phone_number" className="block text-sm font-medium mb-1">
                                                Phone Number
                                            </label>
                                            <input
                                                type="text"
                                                id="phone_number"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value)}
                                                className="w-full px-4 py-2 rounded-md bg-blue-900 border border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="message" className="block text-sm font-medium mb-1">
                                                Message
                                            </label>
                                            <textarea
                                                id="message"
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                rows="4"
                                                className="w-full px-4 py-2 rounded-md bg-blue-900 border border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <button
                                            onClick={() => handleSubmit()}
                                            disabled={contactFillMutation?.isPending}
                                            className="px-8 bg-blue-900 text-white font-medium py-2 rounded-full">
                                            {contactFillMutation?.isPending ? 'Sending' : 'Send'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
                {/* Footer */}
                <footer className="pt-8">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                            <img className="h-12" src={logoLight} alt="ViewPort Logo" />
                            <div className="flex flex-wrap justify-center gap-4 text-sm mt-4 md:mt-0">
                                <a href="/" className="hover:text-blue-300">Home</a>
                                <a href="/lander#about" className="hover:text-blue-300">About Us</a>
                                <a href="/lander#services" className="hover:text-blue-300">Services</a>
                                <a href="/subscriptions" className="hover:text-blue-300">Subscriptions</a>
                                <a href="/lander#contact" className="hover:text-blue-300">Contact Us</a>
                                <a href="/lander#faq" className="hover:text-blue-300">FAQ</a>
                                <a href="/privacy" className="hover:text-blue-300">Privacy Policy</a>
                                <a href="/terms" className="hover:text-blue-300">Terms</a>
                            </div>
                        </div>
                    </div>
                    <div className="bg-blue-900 h-[50px] w-full text-center text-sm text-blue-300 leading-[50px]">
                        © 2025 - All Rights Reserved - ViewPort | Designed & Developed by SolidAppMaker
                    </div>
                </footer>
            </div>
        </div>
    );
};
export default LandingPage;